export interface AppDefinition {
  id: string;
  name: string;
  dept: string;
  allowedDomains: string[];
  urlPatterns: RegExp[];
}

export const REGISTERED_APPS: AppDefinition[] = [
  {
    id: "operations-hub",
    name: "Operations Hub",
    dept: "Operations",
    allowedDomains: [
      "ops.mlconnections.com",
      "mlc-ops-hub.web.app",
      "mlc-ops-hub.firebaseapp.com",
    ],
    urlPatterns: [/ops\.mlconnections\.com/i, /mlc-ops-hub/i],
  },
  {
    id: "mlc-hr-tools",
    name: "HR Tools",
    dept: "Human Resources",
    allowedDomains: [
      "hr.mlconnections.com",
      "mlc-hr-tools.web.app",
      "mlc-hr-tools.firebaseapp.com",
    ],
    urlPatterns: [/hr\.mlconnections\.com/i, /mlc-hr-tools/i],
  },
  {
    id: "viq-autostart",
    name: "VIQ Autostart",
    dept: "Engineering",
    allowedDomains: [
      "viq.mlconnections.com",
      "viq-autostart.web.app",
      "viq-autostart.firebaseapp.com",
    ],
    urlPatterns: [/viq\.mlconnections\.com/i, /viq-autostart/i],
  },
  {
    id: "memoq-bridge",
    name: "memoQ Bridge",
    dept: "Translation Tech",
    allowedDomains: [
      "memoq.mlconnections.com",
      "memoq-bridge.web.app",
      "memoq-bridge.firebaseapp.com",
    ],
    urlPatterns: [/memoq\.mlconnections\.com/i, /memoq-bridge/i],
  },
  {
    id: "teppi-portal",
    name: "Teppi Portal",
    dept: "Customer Success",
    allowedDomains: [
      "teppi.mlconnections.com",
      "teppi-portal.web.app",
      "teppi-portal.firebaseapp.com",
      "teppy.mlconnections.com",
      "teppy-portal.web.app",
      "teppy-portal.firebaseapp.com",
    ],
    urlPatterns: [/tepp[iy]\.mlconnections\.com/i, /tepp[iy]-portal/i],
  },
  {
    id: "sales-pipeline",
    name: "Sales Pipeline",
    dept: "Sales",
    allowedDomains: [
      "sales.mlconnections.com",
      "smdt-sales-app.web.app",
      "smdt-sales-app.firebaseapp.com",
    ],
    urlPatterns: [/sales\.mlconnections\.com/i, /smdt-sales-app/i],
  },
  {
    id: "mlc-bug-tracking",
    name: "Ticketing Tool",
    dept: "Engineering / IT",
    allowedDomains: [
      "tickets.multilingualconnections.com",
      "tickets.mlconnections.com",
      "bugs.multilingualconnections.com",
      "bugs.mlconnections.com",
      "mlc-bug-tracking.web.app",
      "mlc-bug-tracking.firebaseapp.com",
    ],
    urlPatterns: [
      /tickets\.multilingualconnections\.com/i,
      /tickets\.mlconnections\.com/i,
      /bugs\.multilingualconnections\.com/i,
      /bugs\.mlconnections\.com/i,
      /mlc-bug-tracking/i,
    ],
  },
];

export interface DetectAppOptions {
  appId?: string;
  appName?: string;
  currentUrl?: string;
}

export interface DetectedApp {
  id: string;
  name: string;
}

const findRegisteredApp = (id?: string | null): AppDefinition | undefined => {
  if (!id) return undefined;
  const normalized = id.toLowerCase().trim();
  return REGISTERED_APPS.find(
    (a) =>
      a.id === normalized ||
      (normalized === "teppy-portal" && a.id === "teppi-portal"),
  );
};

/**
 * Automatically detects the source application based on explicit props,
 * DOM/window injection, hostname/URL matching, or query params.
 */
export function detectSourceApp(options?: DetectAppOptions): DetectedApp {
  // 1. Explicit prop passed
  if (options?.appId && options.appId !== "all") {
    const matched = findRegisteredApp(options.appId);
    if (matched) {
      return { id: matched.id, name: options.appName || matched.name };
    }
    return {
      id: options.appId,
      name:
        options.appName ||
        options.appId
          .split("-")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" "),
    };
  }

  // 2. Window / DOM injection check (for host applications embedding the widget/modal)
  if (typeof window !== "undefined") {
    const globalAppId = (window as unknown as { __MLC_APP_ID__?: string })
      .__MLC_APP_ID__;
    if (globalAppId) {
      const matched = findRegisteredApp(globalAppId);
      if (matched) return { id: matched.id, name: matched.name };
    }

    if (typeof document !== "undefined") {
      const metaTag = document.querySelector('meta[name="mlc-app-id"]');
      const metaContent = metaTag?.getAttribute("content");
      if (metaContent) {
        const matched = findRegisteredApp(metaContent);
        if (matched) return { id: matched.id, name: matched.name };
      }

      const datasetAppId = document.documentElement?.dataset?.mlcAppId;
      if (datasetAppId) {
        const matched = findRegisteredApp(datasetAppId);
        if (matched) return { id: matched.id, name: matched.name };
      }
    }
  }

  // 3. Evaluate URL / hostname matching
  const urlToEvaluate =
    options?.currentUrl ||
    (typeof window !== "undefined" ? window.location.href : "");

  if (urlToEvaluate) {
    try {
      const parsedUrl = new URL(urlToEvaluate, "https://mlconnections.com");
      const hostname = parsedUrl.hostname.toLowerCase();

      // Check allowedDomains & urlPatterns
      for (const app of REGISTERED_APPS) {
        if (
          app.allowedDomains.some(
            (d) => hostname === d || hostname.endsWith(`.${d}`),
          )
        ) {
          return { id: app.id, name: app.name };
        }
        if (app.urlPatterns.some((pattern) => pattern.test(urlToEvaluate))) {
          return { id: app.id, name: app.name };
        }
      }

      // Check URL search parameters e.g. ?appId=mlc-hr-tools
      const searchParamAppId =
        parsedUrl.searchParams.get("appId") ||
        parsedUrl.searchParams.get("app");
      if (searchParamAppId) {
        const matched = findRegisteredApp(searchParamAppId);
        if (matched) return { id: matched.id, name: matched.name };
      }
    } catch {
      // Ignore URL parsing errors and fall through
    }
  }

  // 4. Default fallback: Operations Hub
  const fallback = REGISTERED_APPS[0]!;
  return { id: fallback.id, name: fallback.name };
}
